import { createContext, type ReactNode, useContext } from "react";
import type {
  Character,
  Encounter,
  GeneratorSettings,
  Location,
  LockState,
  Objective,
  SideQuest,
  Skill,
  StorySeed,
  StorySeedDrafts,
} from "@/types/story.ts";

export interface StoryPlaygroundContextValue {
  settings: GeneratorSettings;
  storySeed: StorySeed;
  drafts: StorySeedDrafts;
  onGenerateTwist: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<string[]>;
  onGenerateIntro: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<string[]>;
  onGenerateSideQuest: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<SideQuest[]>;
  onGenerateCharacter: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<Character[]>;
  onGenerateLocation: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<Location[]>;
  onGenerateEncounter: (mode?: "replace" | "append", forceFresh?: boolean) => Promise<Encounter[]>;
  onGenerateStoryNames: (options?: { forceFresh?: boolean }) => Promise<string[]>;
  onGenerateMainObjective: (options?: { forceFresh?: boolean }) => Promise<void>;
  onGenerateStep4Bulk: (options?: { forceFresh?: boolean }) => Promise<void>;
  onUpdateDraftOptions: (
    field: keyof StorySeedDrafts,
    options: StorySeedDrafts[keyof StorySeedDrafts]["options"],
  ) => void;
  onSelectSuggestion: (
    field: keyof StorySeedDrafts,
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
  ) => void;
  onRemoveSuggestion: (
    field: keyof StorySeedDrafts,
    item: Character | Location | Skill | SideQuest | Objective | Encounter | string,
  ) => void;
  onToggleLock: (field: keyof LockState, index?: number) => void;
  onUpdateSettings: (settings: Partial<GeneratorSettings>) => void;
  onUpdateStoryName: (name: string) => void;
  onUpdateStoryIntro: (intro: string) => void;
  onUpdateSideQuest: (slotIndex: number, sideQuest: Partial<SideQuest>) => void;
  onUpdateCharacter: (slotIndex: number, character: Partial<Character>) => void;
  onUpdateLocation: (slotIndex: number, location: Partial<Location>) => void;
  onUpdateEncounter: (slotIndex: number, encounter: Partial<Encounter>) => void;
  onUpdateSlotCount: (
    field: "sideQuests" | "characters" | "locations" | "encounters",
    delta: number,
  ) => void;
  onUpdateMainObjective: (objective: Partial<Objective>) => void;
  onUpdateStoryTwist: (storyTwist: string) => void;
  onGenerateSkillUses: (
    itemType: "objective" | "sideQuest" | "character" | "location" | "encounter",
    itemIndex?: number,
    options?: { forceFresh?: boolean; targetDescription?: string },
  ) => Promise<string[]>;
  generatingFields: Partial<Record<string, boolean>>;
  onBack: () => void;
}

const StoryPlaygroundContext = createContext<StoryPlaygroundContextValue | null>(null);

export function StoryPlaygroundProvider(
  { value, children }: { value: StoryPlaygroundContextValue; children: ReactNode },
) {
  return <StoryPlaygroundContext.Provider value={value}>{children}
  </StoryPlaygroundContext.Provider>;
}

export function useStoryPlaygroundContext(): StoryPlaygroundContextValue {
  const context = useContext(StoryPlaygroundContext);
  if (!context) {
    throw new Error("useStoryPlaygroundContext must be used within StoryPlaygroundProvider");
  }
  return context;
}
